import React, { useEffect, useState } from "react"
import {
  Box, Menu, MenuButton, MenuItemOption, MenuList, MenuOptionGroup, Stack, Text,
} from "@chakra-ui/react"
import Icon from "components/elements/Icon"
import { useInfiniteQuery } from "react-query"
import { getEventQuestions } from "api/forms"
import { FormQuestion, QuestionTypes } from "sharedTypes"
import { CHART_COLORS } from "utilities/constants"
import { responseSlices, responseTotal } from "utilities/questionResponses"
import { eventQuestionsPath } from "utilities/routes"
import DashboardCard, { EmptyState, LinkAction } from "../components/DashboardCard"
import { Legend, Segment, StackedBar } from "../components/charts"

const CHARTABLE = [
  QuestionTypes.SingleAnswer,
  QuestionTypes.MultipleAnswer,
  QuestionTypes.DropdownMenu,
  QuestionTypes.Terms,
]

// Same slices, order and colours as the Submissions > Questions charts.
const segments = (question: FormQuestion): Segment[] => responseSlices(question)
  .map(({ name, value }, index) => ({
    label: name,
    value,
    color: CHART_COLORS[index % CHART_COLORS.length],
  }))

const plainTitle = (title: string) => title.replace(/<[^>]+>/g, "").trim()

const answered = (question: FormQuestion) => Object.entries(question.responses ?? {})
  .filter(([raw]) => raw !== "[]")
  .reduce((sum, [, count]) => sum + count, 0)

type Props = {
  eventId: string
  onSetUp: () => void
}

const QuestionsCard = ({ eventId, onSetUp }: Props) => {
  // Own cache key, so loading every page here doesn't change other pages' question lists.
  const { data, fetchNextPage, hasNextPage } = useInfiniteQuery(
    ["dashboardQuestions", eventId],
    ({ pageParam = 1 }) => getEventQuestions(eventId, pageParam),
    { getNextPageParam: (lastPage) => lastPage.data.meta.nextPage ?? undefined },
  )
  const questions: FormQuestion[] = data?.pages.flatMap((page) => page.data.data) ?? []

  useEffect(() => {
    if (hasNextPage) {
      fetchNextPage()
    }
  }, [hasNextPage, questions.length, fetchNextPage])

  const chartable = questions
    .filter(({ type, isPlaceholder }) => CHARTABLE.includes(type) && !isPlaceholder)
  const [selectedId, setSelectedId] = useState<string>()
  const question = chartable.find(({ id }) => id === selectedId)
    ?? chartable.find((q) => answered(q) > 0)
    ?? chartable[0]

  if (!question) {
    return (
      <DashboardCard title="Questions">
        <EmptyState
          title="No questions yet"
          body="Add questions to your forms to see answers here."
          cta="Set up questions"
          onClick={onSetUp}
        />
      </DashboardCard>
    )
  }

  const items = segments(question).filter(({ value }) => value > 0)

  return (
    <DashboardCard title="Questions">
      <Menu placement="bottom-start">
        <MenuButton textAlign="left" w="full">
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2}>
            <Text fontWeight="semibold" lineHeight="short">{plainTitle(question.title)}</Text>
            <Icon icon="down-arrow" size={4} mt={1} color="gray.500" flexShrink={0} />
          </Box>
          <Text fontSize="xs" color="gray.600" mt={0.5}>
            {answered(question).toLocaleString()} responses · {question.type}
          </Text>
        </MenuButton>
        <MenuList maxW="sm" zIndex="dropdown">
          <MenuOptionGroup
            type="radio"
            value={question.id}
            onChange={(value) => setSelectedId(value as string)}
            title="Show question"
          >
            {chartable.map(({ id, title }) => (
              <MenuItemOption key={id} value={id} fontSize="sm">{plainTitle(title)}</MenuItemOption>
            ))}
          </MenuOptionGroup>
        </MenuList>
      </Menu>
      {items.length ? (
        <Stack spacing={3} mt={3}>
          <StackedBar items={items} />
          <Legend items={items} total={responseTotal(question)} />
        </Stack>
      ) : (
        <Text color="gray.600" mt={3}>No answers yet.</Text>
      )}
      <Box mt="auto" pt={4}>
        <LinkAction to={eventQuestionsPath(eventId)} chevron>View all questions</LinkAction>
      </Box>
    </DashboardCard>
  )
}

export default QuestionsCard
